import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./JudgmentUploadModal.css";
import { apiFetch } from "../utils/apifetch";

const API = import.meta.env.VITE_API_URL || "";

export default function JudgmentUploadModal({
  mode,
  judgmentData,
  onClose,
  onJudgmentSaved
}) {
  const navigate = useNavigate();
  const isEdit = mode === "edit";
  const currentYear = new Date().getFullYear();

  const [journals, setJournals] = useState([]);
  const [judges, setJudges] = useState([]);
  const [actsMaster, setActsMaster] = useState([]);
  const [selectedJudges, setSelectedJudges] = useState([]);
  const [acts, setActs] = useState([{ ActId: "", SectionNumbers: "" }]);
  const today = new Date().toISOString().split("T")[0];

  const [success, setSuccess] = useState("");
  const [formData, setFormData] = useState({
    JournalShortName: "",
    YearPublished: "",
    VolumeNo: "",
    PageNo: "",
    HighCourtName: "IN THE HIGH COURT OF MADRAS",
    PlaceOfCourt: "Madras",
    CaseType: "",
    JudgmentDate: today,
    PetitionersDetails: "",
    RespondentsDetails: "",
    Headnotes: "",
    CreatedByUid: ""
  });

  const [pdfFile, setPdfFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isEdit || !judgmentData) return;

    setFormData({
      JournalShortName: judgmentData.JournalShortName || "",
      YearPublished: judgmentData.YearPublished || "",
      VolumeNo: judgmentData.VolumeNo || "",
      PageNo: judgmentData.PageNo || "",
      HighCourtName: judgmentData.HighCourtName || "",
      PlaceOfCourt: judgmentData.PlaceOfCourt || "Madras",
      CaseType: judgmentData.CaseType || "",
      JudgmentDate: judgmentData.JudgmentDate
        ? judgmentData.JudgmentDate.split("T")[0]
        : "",
      PetitionersDetails: judgmentData.PetitionersDetails || "",
      RespondentsDetails: judgmentData.RespondentsDetails || "",
      Headnotes: judgmentData.Headnotes || "",
      CreatedByUid: judgmentData.CreatedByUid || ""
    });

    if (judgmentData.Judges) {
      setSelectedJudges(judgmentData.Judges.map(j => j.Id));
    }

    if (judgmentData.Acts) {
      setActs(
        judgmentData.Acts.map(a => ({
          ActId: a.Id,
          SectionNumbers: a.SectionNumbers || ""
        }))
      );
    }
  }, [isEdit, judgmentData]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const res1 = await apiFetch("/journal");
        const journalsData = await res1.json();
        setJournals(journalsData);

        const res2 = await apiFetch("/judges");
        const judgesData = await res2.json();
        setJudges(judgesData);

        const res3 = await apiFetch("/act");
        const actsData = await res3.json();
        setActsMaster(actsData);
      } catch (err) {
        console.error("Error loading dropdown data:", err);
        setError("Unable to load dropdown data.");
      }
    };
    loadData();
  }, []);

  const handleChange = e => {
    const { name, value } = e.target;
    setError("");
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleJudge = id => {
    const judgeId = Number(id);
    setSelectedJudges(prev => {
      if (prev.includes(judgeId)) return prev.filter(j => j !== judgeId);
      return [...prev, judgeId];
    });
  };

  const handleCaseTypeFocus = () => {
    if (!formData.PageNo) {
      setError("Please enter Page Number first.");
      return;
    }
    if (selectedJudges.length === 0) {
      setError("Please select at least one Judge before entering Case Type.");
      document.querySelector(".judge-list")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const addActRow = () => {
    setActs([...acts, { ActId: "", SectionNumbers: "" }]);
  };

  const removeActRow = index => {
    const updated = [...acts];
    updated.splice(index, 1);
    setActs(updated);
  };

  const updateAct = (index, field, value) => {
    const updated = [...acts];
    updated[index][field] = value;
    setActs(updated);
  };

  const handleFileChange = e => {
    setPdfFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.CaseType || !formData.CaseType.trim()) {
      setError("Case Type is required");
      return;
    }

    if (!isEdit && !pdfFile) {
      setError("Please upload the judgment PDF.");
      return;
    }

    if (selectedJudges.length === 0) {
      setError("Please select at least one judge.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const data = new FormData();
      Object.entries(formData).forEach(([k, v]) => {
        data.append(k, v);
      });

      data.append("judges", JSON.stringify(selectedJudges));
      const cleanActs = acts.filter(a => a.ActId);
      data.append("acts", JSON.stringify(cleanActs));

      if (pdfFile) {
        data.append("pdf", pdfFile);
      }

      const url = isEdit ? `/judgments/${judgmentData.Id}` : "/judgments";
      const method = isEdit ? "PUT" : "POST";

      const res = await apiFetch(url, {
        method,
        body: data
      });

      if (!res.ok) {
        const text = await res.text();
        let message = "Upload failed";
        try {
          const err = JSON.parse(text);
          message = err.message || message;
        } catch {
          message = text || message;
        }
        throw new Error(message);
      }

      setSuccess(isEdit ? "Judgment updated successfully." : "Judgment uploaded successfully.");
      onJudgmentSaved?.();
      onClose?.();
    } catch (err) {
      setError(err.message || "Unable to upload judgment.");
    } finally {
      setLoading(false);
    }
  };

  // HELPER FUNCTIONS FOR RENDERING
  function formatDateDDMMMYYYY(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, "0");
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${day}-${months[d.getMonth()]}-${d.getFullYear()}`;
  }

  function generateCitation() {
    const { JournalShortName, YearPublished, VolumeNo, PageNo } = formData;
    if (!JournalShortName || !YearPublished || !VolumeNo || !PageNo) return "";
    return `(${YearPublished}) ${VolumeNo} ${JournalShortName} ${PageNo}`;
  }

  function getSelectedJudgesList() {
    if (!Array.isArray(judges)) return [];
    return judges.filter(j => selectedJudges.includes(Number(j.id)));
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content with-nav">
        <aside className="modal-nav">
          <ul>
            <li><a href="#case-info">Case Info</a></li>
            <li><a href="#court-info">Court Info</a></li>
            <li><a href="#parties">Parties</a></li>
            <li><a href="#acts">Acts</a></li>
            <li><a href="#upload">Upload</a></li>
          </ul>
        </aside>

        <div className="modal-form">
          <h2>{isEdit ? "Edit Judgment" : "Upload New Judgment"}</h2>
          {success && <div className="success">{success}</div>}
          {error && <div className="error">{error}</div>}

          <form onSubmit={handleSubmit}>
            <section id="case-info">
              <label>Journal</label>
              <select name="JournalShortName" value={formData.JournalShortName} onChange={handleChange} required>
                <option value="">Select Journal ▼</option>
                {Array.isArray(journals) && journals.map(j => (
                  <option key={j.id} value={j.shortName}>{j.shortName}</option>
                ))}
              </select>

              <label>Year Published</label>
              <input type="number" name="YearPublished" value={formData.YearPublished} onChange={handleChange} required />

              <label>Volume No</label>
              <input type="text" name="VolumeNo" value={formData.VolumeNo} onChange={handleChange} maxLength={10} required />

              <label>Page No</label>
              <input type="number" name="PageNo" value={formData.PageNo} onChange={handleChange} min="1" required />

              {generateCitation() && (
                <div className="citation-preview">Citation Preview: <strong>{generateCitation()}</strong></div>
              )}
            </section>

            <section id="court-info">
              <label>High Court</label>
              <input type="text" name="HighCourtName" value={formData.HighCourtName} onChange={handleChange} />

              <label>Place of Court</label>
              <select name="PlaceOfCourt" value={formData.PlaceOfCourt} onChange={handleChange}>
                <option value="Madras">Madras</option>
                <option value="Madurai Bench">Madurai Bench</option>
              </select>

              <label>Judges ▼</label>
              <div className="judge-list">
                {Array.isArray(judges) && judges.map(j => {
                  let fullName = `${j.judgeTitle || ""} ${j.judgeInitial || ""} ${j.judgeName || ""}`.replace(/\s+/g, ' ').trim();
                  const truncatedName = fullName.length > 35 ? fullName.substring(0, 35) + "..." : fullName;
                  return (
                    <label key={j.id} className="judge-option">
                      <input type="checkbox" checked={selectedJudges.includes(j.id)} onChange={() => toggleJudge(j.id)} />
                      <span className="judge-name">{truncatedName}</span>
                    </label>
                  );
                })}
              </div>

              {getSelectedJudgesList().length > 0 && (
                <div className="bench-preview">
                  <strong>Bench</strong>
                  <ol>
                    {getSelectedJudgesList().map(j => (
                      <li key={j.id}>{j.judgeTitle} {j.judgeInitial} {j.judgeName}</li>
                    ))}
                  </ol>
                </div>
              )}

              <label>Case Type</label>
              <input type="text" name="CaseType" value={formData.CaseType} onChange={handleChange} onFocus={handleCaseTypeFocus} required />

              <label>Judgment Date</label>
              <input type="date" name="JudgmentDate" value={formData.JudgmentDate} onChange={handleChange} required />
              {formData.JudgmentDate && (
                <div className="date-preview">Selected Date: {formatDateDDMMMYYYY(formData.JudgmentDate)}</div>
              )}
            </section>

            <section id="parties">
              <label>Petitioners Details</label>
              <textarea name="PetitionersDetails" value={formData.PetitionersDetails} onChange={handleChange} required />
              
              <label>Respondents Details</label>
              <textarea name="RespondentsDetails" value={formData.RespondentsDetails} onChange={handleChange} required />

              <label>Headnotes</label>
              <textarea name="Headnotes" value={formData.Headnotes} onChange={handleChange} required />
            </section>

            <section id="acts">
              <label>Acts and Sections</label>
              {acts.map((a, index) => (
                <div key={index} className="act-row">
                  <select value={a.ActId} onChange={e => updateAct(index, "ActId", Number(e.target.value))}>
                    <option value="">Select Act ▼</option>
                    {Array.isArray(actsMaster) && actsMaster.map(act => (
                      <option key={act.id} value={act.id}>{act.shortName}</option>
                    ))}
                  </select>
                  <input type="text" placeholder="Enter Section Numbers" value={a.SectionNumbers} onChange={e => updateAct(index, "SectionNumbers", e.target.value)} />
                  {acts.length > 1 && (
                    <button className="btn btn-danger" type="button" onClick={() => removeActRow(index)}>Remove</button>
                  )}
                </div>
              ))}
              <button className="btn btn-success" type="button" onClick={addActRow}>+ Add Another Act</button>
            </section>

            <section id="upload">
              <label>Upload Judgment PDF</label>
              <input type="file" accept="application/pdf" onChange={handleFileChange} required={!isEdit} />
            </section>

            <div className="modal-actions">
              <button className="btn btn-primary" type="submit" disabled={loading}>
                {loading ? "Saving..." : isEdit ? "Update" : "Upload"}
              </button>
              <button className="btn btn-secondary" type="button" onClick={onClose}>Cancel</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}